// Shared detection-label renderer.
//
// Draws label chips offset away from their marker with a short leader line, and
// applies simple greedy collision avoidance so chips don't stack on top of each
// other. Used by both the read-only overlay canvas (ProspectiveDataAnnotations)
// and the editor's CanvasDrawingManager so labels look identical in both
// contexts.
//
// All coordinates and dimensions passed in are in display (canvas) pixels.

const FONT_FAMILY = '"Lato", Arial, sans-serif'
const PADDING = 5 // horizontal padding inside the chip
const VPAD = 3 // vertical padding inside the chip
const GAP = 6 // gap between the chip and its anchor point

function labelFontSize (canvasWidth) {
  return Math.max(11, Math.round(canvasWidth * 0.013))
}

function applyFont (ctx, fontSize) {
  ctx.font = `${fontSize}px ${FONT_FAMILY}`
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
}

function measureChip (ctx, text, fontSize) {
  const textW = ctx.measureText(text).width
  return {
    w: Math.ceil(textW) + PADDING * 2,
    h: Math.ceil(fontSize) + VPAD * 2
  }
}

// Compute a chip rect placed centred above the anchor, flipped below if there's
// no room above, and clamped horizontally within the canvas.
function computeRect (ctx, label, canvasW, canvasH, fontSize) {
  const { w, h } = measureChip(ctx, label.text, fontSize)
  let x = label.anchorX - w / 2
  let y = label.anchorY - GAP - h
  if (y < 2) y = label.anchorY + GAP

  const maxX = canvasW - 2 - w
  if (maxX < 2) x = 2
  else x = Math.max(2, Math.min(x, maxX))

  return { x, y, w, h, anchorX: label.anchorX, anchorY: label.anchorY }
}

function overlaps (a, b) {
  return !(
    a.x + a.w <= b.x ||
    b.x + b.w <= a.x ||
    a.y + a.h <= b.y ||
    b.y + b.h <= a.y
  )
}

// Greedy vertical separation: push overlapping labels downward until they clear
// each other, clamped to the canvas. Iteration-capped to guarantee termination.
// Leader lines (drawn later) keep each chip associated with its anchor even when
// it is shifted away.
// Greedy separation: push labels apart from each other AND out of any marker
// (finding) area, clamped to the canvas. Movement is downward only, so each
// label's y is monotonic and bounded — guaranteeing termination. Leader lines
// (drawn later) keep each chip associated with its anchor even when shifted.
function resolveCollisions (rects, markers, canvasH) {
  const order = rects
    .map((_, i) => i)
    .sort(
      (i, j) =>
        rects[i].anchorY - rects[j].anchorY ||
        rects[i].anchorX - rects[j].anchorX
    )

  const clampBottom = (r) => {
    const maxY = canvasH - 2 - r.h
    if (r.y > maxY) r.y = Math.max(2, maxY)
  }

  let changed = true
  let iters = 0
  while (changed && iters < 30) {
    changed = false
    iters++
    // label vs label
    for (let a = 0; a < order.length; a++) {
      for (let b = a + 1; b < order.length; b++) {
        const ra = rects[order[a]]
        const rb = rects[order[b]]
        if (!overlaps(ra, rb)) continue
        const before = rb.y
        rb.y += ra.y + ra.h - rb.y + 1
        clampBottom(rb)
        if (rb.y !== before) changed = true
      }
    }
    // label vs finding area (marker): push the label clear, below the marker
    for (let i = 0; i < order.length; i++) {
      const r = rects[order[i]]
      for (let m = 0; m < markers.length; m++) {
        const mk = markers[m]
        if (!overlaps(r, mk)) continue
        const before = r.y
        r.y += mk.y + mk.h - r.y + 1
        clampBottom(r)
        if (r.y !== before) changed = true
      }
    }
  }
}

function drawChip (ctx, rect, fontSize) {
  const { x, y, w, h } = rect
  const mk = rect.marker

  // Leader: connect the nearest points of the chip and its finding marker, so
  // the tick stays short even when the chip has been pushed to the far side of
  // the marker (e.g. below a box). Falls back to the stored anchor point when no
  // marker is available.
  let fromX, fromY, toX, toY
  if (mk) {
    const cx = x + w / 2
    const cy = y + h / 2
    // nearest point on the marker to the chip's centre
    fromX = Math.max(mk.x, Math.min(cx, mk.x + mk.w))
    fromY = Math.max(mk.y, Math.min(cy, mk.y + mk.h))
    // nearest point on the chip back to that marker point
    toX = Math.max(x, Math.min(fromX, x + w))
    toY = Math.max(y, Math.min(fromY, y + h))
  } else {
    const chipTop = y
    const chipBottom = y + h
    let edgeY
    if (rect.anchorY <= chipTop) edgeY = chipTop
    else if (rect.anchorY >= chipBottom) edgeY = chipBottom
    else edgeY = rect.anchorY - chipTop < chipBottom - rect.anchorY ? chipTop : chipBottom
    fromX = rect.anchorX
    fromY = rect.anchorY
    toX = Math.max(x, Math.min(rect.anchorX, x + w))
    toY = edgeY
  }

  ctx.strokeStyle = rect.colour
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(fromX, fromY)
  ctx.lineTo(toX, toY)
  ctx.stroke()

  // Chip background.
  ctx.fillStyle = rect.colour
  ctx.fillRect(x, y, w, h)

  // Label text.
  applyFont(ctx, fontSize)
  ctx.fillStyle = '#ffffff'
  ctx.fillText(rect.text, x + PADDING, y + h / 2)
}

// labels: [{ text, colour, anchorX, anchorY }] in display coords.
// markers: [{ x, y, w, h }] in display coords — finding areas labels must avoid.
export function drawLabels (ctx, labels, canvasW, canvasH, markers = []) {
  if (!labels || labels.length === 0) return

  const fontSize = labelFontSize(canvasW)
  applyFont(ctx, fontSize)

  const rects = labels.map((l) => {
    const r = computeRect(ctx, l, canvasW, canvasH, fontSize)
    r.text = l.text
    r.colour = l.colour
    r.marker = l.marker || null
    return r
  })

  resolveCollisions(rects, markers, canvasH)

  for (const r of rects) drawChip(ctx, r, fontSize)
}
